// tinymce-loader lazily injects the self-hosted TinyMCE script into the
// document, on demand, exactly once — no matter how many editor components ask
// for it — and resolves once the global `tinymce` is available.

// Types ship with the self-hosted bundle, pinned to the same version as TINYMCE_SRC.
import type { TinyMCE } from '../../public/assets/tinymce/6.8.5/js/tinymce/tinymce';

export type { TinyMCE };

declare global {
  interface Window {
    tinymce?: TinyMCE;
  }
}

// SCRIPT_MARKER identifies the injected `<script>` so a repeat call — or a page
// that already carries the tag — reuses it rather than adding a duplicate.
const SCRIPT_MARKER = 'data-tinymce-loader';

// TINYMCE_SRC is the self-hosted TinyMCE bundle linced-common serves from its
// public tree (see index.js `treeForPublic`). It's hardcoded on purpose: we
// self-host a single pinned version, same-origin, for every consuming app, so
// there is nothing to configure per app and no CDN/SRI to account for. Bump
// this path (and the public/ assets) together when upgrading TinyMCE.
const TINYMCE_SRC = '/assets/tinymce/6.8.5/js/tinymce/tinymce.min.js';

// loadPromise memoises the in-flight (or settled) load so concurrent callers
// share one injection. It is reset to null on failure so a later attempt can
// retry rather than caching the rejection forever.
let loadPromise: Promise<TinyMCE> | null = null;

// loadTinyMCE ensures the self-hosted TinyMCE bundle is present and returns a
// promise resolving to the global `tinymce`. Safe to call from every editor
// instance; only the first call injects the script.
export function loadTinyMCE(src = TINYMCE_SRC): Promise<TinyMCE> {
  // Already available (script previously loaded, or provided by other means):
  // resolve immediately.
  if (typeof window !== 'undefined' && typeof window.tinymce !== 'undefined') {
    return Promise.resolve(window.tinymce);
  }

  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise<TinyMCE>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[${SCRIPT_MARKER}]`,
    );
    const script = existing || document.createElement('script');

    // The bundle defines the global as it loads.
    const onLoad = () => resolve(window.tinymce as TinyMCE);
    const onError = () => {
      // Don't cache a failure — allow a subsequent mount to retry.
      loadPromise = null;
      script.removeEventListener('load', onLoad);
      reject(new Error(`Failed to load TinyMCE from ${src}`));
    };

    script.addEventListener('load', onLoad, { once: true });
    script.addEventListener('error', onError, { once: true });

    // Only configure and append when we created the element. When a marked tag
    // already exists we just attach listeners and wait for its load.
    if (!existing) {
      script.type = 'text/javascript';
      script.src = src;
      script.async = true;
      script.setAttribute(SCRIPT_MARKER, '');
      document.head.appendChild(script);
    }
  });

  return loadPromise;
}

// _resetForTests clears the memoised promise so each test starts from a clean
// slate. Intended for test teardown only.
export function _resetForTests() {
  loadPromise = null;
}
